/*
  Wishlist page — add to cart
  ---------------------------
  Swish owns the card CTA on /apps/wishlist. After it adds, it sends the shopper to
  /cart. The theme's drawer opens off `cart:updated` from `window.themeCart`, which
  Swish never emits.

  On this page only, the CTA is taken before Swish's handler. A card whose product has
  options opens the theme's quick add sheet instead of adding Swish's variant: the sheet
  withholds the sourcing option and resolves it against stock, which Swish's own picker
  cannot do. A card with nothing to choose still adds straight through themeCart.

  If Swish still completes an add (Add all, or a CTA we could not read a variant from),
  its success events refresh the cart and a short-lived navigation guard drops the /cart
  redirect.
*/
(function () {
  if (window.__wishlistPageCart) return;
  window.__wishlistPageCart = true;

  var SOURCE = 'wishlist-page';
  var CARD = 'wishlist-product-card';
  var CTA = '.wk-cta-button';
  var ADD_ALL = '.wk-add-all, .wk-buy-all, .wk-add-all-to-cart, [class*="wk-add-all"]';
  var OPTION_CONTROLS = 'wk-option-select, wk-option-swatches';
  var READY_TIMEOUT = 10000;
  var GUARD_MS = 4000;

  var armedUntil = 0;

  function arm() {
    armedUntil = Date.now() + GUARD_MS;
  }

  function isArmed() {
    return Date.now() < armedUntil;
  }

  function isCartPageUrl(href) {
    try {
      var path = new URL(href, window.location.origin).pathname.replace(/\/$/, '');
      return path === '/cart' || /\/cart$/.test(path);
    } catch (e) {
      return false;
    }
  }

  function itemFromCard(card) {
    if (!card) return null;

    var id = '';
    var quantity = 1;
    var form = card.querySelector('form');

    if (form) {
      var data = new FormData(form);
      id = data.get('id') || '';
      quantity = parseInt(data.get('quantity'), 10) || 1;
    }

    if (!id) {
      var input = card.querySelector('[name="id"]');
      if (input && input.value) id = input.value;
    }

    if (!id && card.getAttribute) {
      id = card.getAttribute('data-variant-id') || card.getAttribute('variant-id') || '';
    }

    if (!id && card.selectedVariant && card.selectedVariant.id) {
      id = card.selectedVariant.id;
    }

    if (!id && card.variant && card.variant.id) {
      id = card.variant.id;
    }

    if (!id) return null;
    if (!Number.isFinite(quantity) || quantity < 1) quantity = 1;
    return { id: id, quantity: quantity };
  }

  /* Swish links the image and the title at the product. Either is the handle we need;
     the sheet is fetched from it with `?section_id=`. */
  function urlFromCard(card) {
    if (!card) return '';
    var link = card.querySelector('a[href*="/products/"]');
    return link ? link.getAttribute('href') : '';
  }

  /* The sheet is only worth opening when there is something to choose. Swish renders one
     control per option, so their presence is the test — no control, nothing withheld. */
  function hasOptions(card) {
    return !!(card && card.querySelector(OPTION_CONTROLS));
  }

  function openSheet(url, trigger) {
    var event = new CustomEvent('quick-shop:open', {
      detail: { url: url, source: SOURCE },
      cancelable: true,
      bubbles: true,
    });
    (trigger || document).dispatchEvent(event);
    // Cancelled means the sheet took it. Uncancelled means no sheet on the page, and the
    // caller falls back rather than swallowing the click.
    return event.defaultPrevented;
  }

  /* Swish disables its CTA (native `disabled`) until every option it drew is chosen, and a
     disabled button fires no click. Its options are hidden here and its variant is never
     read, so the gate is released rather than answered. Only for cards we route. */
  function releaseGate(scope) {
    var cards = (scope || document).querySelectorAll(CARD);
    for (var i = 0; i < cards.length; i++) {
      if (!hasOptions(cards[i])) continue;
      var btn = cards[i].querySelector(CTA);
      if (btn && btn.disabled) btn.disabled = false;
    }
  }

  function itemsFromPage() {
    var cards = document.querySelectorAll(CARD);
    var items = [];
    for (var i = 0; i < cards.length; i++) {
      var item = itemFromCard(cards[i]);
      if (item) items.push(item);
    }
    return items;
  }

  function addItems(items) {
    if (!items.length || !window.themeCart) return Promise.reject(new Error('Nothing to add'));
    arm();
    return window.themeCart.add(items, SOURCE);
  }

  function announceFromSwish() {
    arm();
    if (window.themeCart) window.themeCart.refresh(SOURCE);
  }

  /* ---- Take the CTA before Swish can redirect -------------------------- */

  document.addEventListener(
    'click',
    function (e) {
      var addAll = e.target.closest && e.target.closest(ADD_ALL);
      if (addAll && addAll.closest('wishlist-page') && window.themeCart) {
        var cards = document.querySelectorAll(CARD);
        var all = itemsFromPage();
        // Leave Add all to Swish unless every card yielded a variant — a partial
        // read would stop Swish and drop the unread rows.
        if (!all.length || all.length !== cards.length) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        if (addAll.__wishlistCartBusy) return;
        addAll.__wishlistCartBusy = true;
        addItems(all).finally(function () {
          addAll.__wishlistCartBusy = false;
        });
        return;
      }

      var btn = e.target.closest && e.target.closest(CTA);
      if (!btn || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
      if (!btn.closest('wishlist-page') || !window.themeCart) return;

      var card = btn.closest(CARD);
      var url = urlFromCard(card);

      // A product with options goes to the sheet, which is the only path that applies the
      // withheld-option rule. Swish's own variant is not read at all on that route.
      if (url && hasOptions(card)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (openSheet(url, btn)) return;
        // No sheet on the page — fall through to the direct add rather than dead-ending.
      }

      var item = itemFromCard(card);
      if (!item) return;

      if (!e.defaultPrevented) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
      if (btn.__wishlistCartBusy) return;
      btn.__wishlistCartBusy = true;
      addItems([item]).finally(function () {
        btn.__wishlistCartBusy = false;
      });
    },
    true
  );

  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form || !form.closest || !form.closest('wishlist-page') || !window.themeCart) return;

      var action = form.getAttribute('action') || form.action || '';
      if (action.indexOf('/cart/add') === -1) return;

      var card = form.closest(CARD);
      var item = itemFromCard(card);
      if (!item) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      addItems([item]).catch(function () {});
    },
    true
  );

  /* ---- If Swish still adds, open the drawer and drop its /cart hop ----- */

  /* Swish re-renders the list on its own — removing a card, paging — so newly drawn cards
     need the same release. Attributes too: it re-disables the CTA as options change. */
  function watchCards() {
    var page = document.querySelector('wishlist-page');
    if (!page) return;
    releaseGate(page);
    new MutationObserver(function () {
      releaseGate(page);
    }).observe(page, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled'],
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchCards);
  } else {
    watchCards();
  }

  document.addEventListener('wk:wishlist:add-to-cart:success', announceFromSwish);
  document.addEventListener('wk:wishlist:add-all-to-cart:success', announceFromSwish);

  function subscribeSwish(app) {
    if (!app || !app.events || typeof app.events.subscribe !== 'function') return;
    app.events.subscribe('wk:wishlist:add-to-cart:success', announceFromSwish);
    app.events.subscribe('wk:wishlist:add-all-to-cart:success', announceFromSwish);
  }

  if (window.WishlistKing) {
    subscribeSwish(window.WishlistKing);
  } else {
    var started = Date.now();
    var timer = setInterval(function () {
      if (window.WishlistKing) {
        clearInterval(timer);
        subscribeSwish(window.WishlistKing);
      } else if (Date.now() - started > READY_TIMEOUT) {
        clearInterval(timer);
      }
    }, 100);
  }

  if (window.navigation && typeof window.navigation.addEventListener === 'function') {
    window.navigation.addEventListener('navigate', function (event) {
      if (!isArmed() || !isCartPageUrl(event.destination.url)) return;
      // intercept() commits a same-document navigation and rewrites the URL to
      // /cart while the wishlist stays on screen. Cancel instead; only intercept
      // (and restore the URL) when the event cannot be cancelled.
      if (event.cancelable) {
        event.preventDefault();
        return;
      }
      if (event.canIntercept) {
        var stay = window.location.href;
        event.intercept({
          handler: function () {
            return Promise.resolve();
          },
        });
        history.replaceState(history.state, '', stay);
      }
    });
  }

  try {
    var assign = window.location.assign.bind(window.location);
    var replace = window.location.replace.bind(window.location);
    window.location.assign = function (url) {
      if (isArmed() && isCartPageUrl(url)) return;
      return assign(url);
    };
    window.location.replace = function (url) {
      if (isArmed() && isCartPageUrl(url)) return;
      return replace(url);
    };

    var hrefDesc =
      Object.getOwnPropertyDescriptor(Location.prototype, 'href') ||
      Object.getOwnPropertyDescriptor(window.location, 'href');
    if (hrefDesc && hrefDesc.get && hrefDesc.set) {
      Object.defineProperty(window.location, 'href', {
        configurable: true,
        enumerable: true,
        get: function () {
          return hrefDesc.get.call(window.location);
        },
        set: function (url) {
          if (isArmed() && isCartPageUrl(url)) return;
          hrefDesc.set.call(window.location, url);
        },
      });
    }
  } catch (e) {
    /* location methods are not writable in every browser; the click intercept is enough */
  }
})();
